import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  getDate,
  getDay,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  setDate,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { Config } from '../config';
import { DatePartName } from '../enums';
import { DatePartItem, DatePickerDay } from '../models';

/**
 * Returns an array of years as DateParts between a range.
 * @param {number} [from=new Date().getFullYear() - 10] - Starting year (default is current year minus 10).
 * @param {number} [to=new Date().getFullYear()] - Ending year (default is current year).
 * @returns {DatePartItem[]} Array of DatePart objects for the specified year range.
 */
export function getYearDataSource(
  from: number = new Date().getFullYear() - 10,
  to: number = new Date().getFullYear()
): DatePartItem[] {
  const result: DatePartItem[] = [];

  for (let year = to; year >= from; year--) {
    result.push({ name: year.toString(), value: year });
  }

  return result;
}

/**
 * Returns the names of the weekdays based on the given locale.
 * @param {Config} [config] - config object with the locale and first week day to use.
 * @returns {string[]} Array of weekday names.
 */
export function getWeekDaysNames(config: Config): string[] {
  return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) =>
    format(
      addDays(startOfWeek(new Date()), (dayOfWeek + config.weekStartsOn) % 7),
      'EEEEEE',
      {
        locale: config.locale,
      }
    )
  );
}

/**
 * Returns an array of months as DateParts.
 * @returns {DatePartItem[]} Array of DatePart objects for all months.
 */
export function getMonthDataSource(config: Config): DatePartItem[] {
  const monthIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  return monthIndices.map((index) => ({
    name: format(new Date(0, index), 'MMMM', { locale: config.locale }),
    value: index,
  }));
}

/**
 * Returns an array of days in a given month as DateParts.
 * @param {Date} date - The reference date for the month.
 * @returns {DatePartItem[]} Array of DatePart objects for all days in the month.
 */
export function getDaysDataSource(date: Date, config: Config): DatePartItem[] {
  const result: DatePartItem[] = [];
  const daysInMonth = endOfMonth(date).getDate();

  for (let i = 1; i <= daysInMonth; i++) {
    result.push({
      name: format(new Date(date.getFullYear(), date.getMonth(), i), 'd', {
        locale: config.locale,
      }),
      value: i,
    });
  }

  return result;
}

/**
 * Returns an array of DatePickers representing days in a month, grouped by weeks.
 * @param {Date} date - The reference date for the month.
 * @param {Day} [weekStartsOn=1] - The day on which the week starts (0 = Sunday, 1 = Monday, etc.).
 * @returns {DatePickerDay[]} Array of DatePickerDay objects for the month.
 */
export function getMonthDates(date: Date, config: Config): DatePickerDay[] {
  const startDate = startOfWeek(startOfMonth(date), {
    weekStartsOn: config.weekStartsOn,
  });
  const endDate = endOfWeek(endOfMonth(date), {
    weekStartsOn: config.weekStartsOn,
  });

  const totalDays = differenceInDays(endDate, startDate) + 1;

  return Array.from({ length: totalDays }, (_, index) => {
    const currentDate = addDays(startDate, index);
    return <DatePickerDay>{
      date: currentDate,
      text: format(currentDate, 'd'),
      disabled: isDayDisabled(currentDate, config),
    };
  });
}

export function isDayDisabled(day: Date, config: Config) {
  let disabled = config.disabledWeekDays.includes(getDay(day));
  disabled ||= isAfter(day, config.max);
  disabled ||= isBefore(day, config.min);
  disabled ||= !!config.disabledDates.find((dd) => isSameDay(dd, day));

  return disabled;
}

/**
 * Groups an array of DatePickers (days) into weeks.
 * @param {DatePickerDay[]} days - The array of days to group.
 * @returns {DatePickerDay[][]} Array of weeks (arrays of DatePickers).
 */
export function getDaysGroupedByWeeks(
  days: DatePickerDay[]
): DatePickerDay[][] {
  const DAYS_PER_WEEK: number = 7;
  const weeks: DatePickerDay[][] = [];

  for (let i = 0; i < days.length; i += DAYS_PER_WEEK) {
    weeks.push(days.slice(i, i + DAYS_PER_WEEK));
  }

  return weeks;
}

/**
 * Returns the next day, month, or year from a given date.
 * @param {Date} date - The reference date.
 * @param {DatePartName} datePart - The part of the date to increase (day, month, or year).
 * @returns {Date} New date with increased day, month, or year.
 */
export function getNextDay(date: Date, datePart: DatePartName): Date {
  return getDayForDatePart(1, date, datePart);
}

/**
 * Returns the previous day, month, or year from a given date.
 * @param {Date} date - The reference date.
 * @param {DatePartName} datePart - The part of the date to decrease (day, month, or year).
 * @returns {Date} New date with decreased day, month, or year.
 */
export function getPrevDay(date: Date, datePart: DatePartName): Date {
  return getDayForDatePart(-1, date, datePart);
}

/**
 * Check if the given date part item matches the current date part value.
 * @param {DatePartItem} datePart - The date part item to check.
 * @returns {boolean} True if the date part item matches, false otherwise.
 */
export function selectDatePart(
  date: Date,
  datePartName: DatePartName,
  datePart: DatePartItem
): boolean {
  let selected = false;
  switch (datePartName) {
    case DatePartName.DAY:
      selected = getDate(date) === datePart.value;
      break;
    case DatePartName.MONTH:
      selected = getMonth(date) === datePart.value;
      break;
    case DatePartName.YEAR:
      selected = getYear(date) === datePart.value;
      break;
  }

  return selected;
}

export function disableDatePart(
  date: Date,
  datePartName: DatePartName,
  datePart: DatePartItem,
  config: Config
) {
  let disabled = false;
  switch (datePartName) {
    case DatePartName.DAY:
      disabled =
        isBefore(setDate(date, datePart.value), config.min) ||
        isAfter(setDate(date, datePart.value), config.max);
      break;
    case DatePartName.MONTH:
      disabled =
        isBefore(endOfMonth(setMonth(date, datePart.value)), config.min) ||
        isAfter(startOfMonth(setMonth(date, datePart.value)), config.max);
      break;
    case DatePartName.YEAR:
      disabled =
        isBefore(endOfYear(setYear(date, datePart.value)), config.min) ||
        isAfter(startOfYear(setYear(date, datePart.value)), config.max);
      break;
  }

  return disabled;
}

/**
 * Adjusts a given date by an interval based on a specified date part (day, month, or year).
 * @param {number} interval - The interval to adjust by.
 * @param {Date} date - The reference date.
 * @param {DatePartName} datePart - The part of the date to adjust.
 * @returns {Date} New adjusted date.
 * @throws Will throw an error if an invalid datePart value is provided.
 */
function getDayForDatePart(
  interval: number,
  date: Date,
  datePart: DatePartName
): Date {
  switch (datePart) {
    case DatePartName.DAY:
      return interval >= 0
        ? addDays(date, interval)
        : subDays(date, Math.abs(interval));
    case DatePartName.MONTH:
      return interval >= 0
        ? addMonths(date, interval)
        : subMonths(date, Math.abs(interval));
    case DatePartName.YEAR:
      return interval >= 0
        ? addYears(date, interval)
        : subYears(date, Math.abs(interval));
    default:
      throw new Error('Invalid datePart value');
  }
}
